import { memo, SyntheticEvent, useMemo, useState } from 'react';

import { isEmpty } from 'remeda';
import useSWR from 'swr';

import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { USER_SEARCH_DEBOUNCE, USER_SEARCH_MIN_LENGTH } from '../constants';
import { getSearchFooter, PaperFooterContext, PaperWithFooter } from '../paperWithFooter/PaperWithFooter';
import { sanitizeUserSearch } from '../utils';

import css from './UserPicker.module.css';

import { userService } from 'v2/api/userService';
import { UserAvatar } from 'v2/components/userAvatar/UserAvatar';
import { useDebounce } from 'v2/hooks/useDebounce';
import { Nullable } from 'v2/types';
import { UserModel } from 'v2/types/UserModel';
import { handleHttpError } from 'v2/utils';

type UserOption = Pick<UserModel, 'id' | 'userNickname' | 'imgUrl' | 'email' | 'telegramUserName' | 'googleData'>;

interface UserPickerProps {
  userId: string;
  onChange: (userId: string) => void;
}

const getUserDetails = (user: UserOption): string[] => {
  const email = user.email ?? user.googleData?.email;

  return [email, user.telegramUserName && `@${user.telegramUserName}`].filter((item): item is string => Boolean(item));
};

export const UserPicker = memo<UserPickerProps>(({ userId, onChange }) => {
  const [query, setQuery] = useState('');
  const [pickedUser, setPickedUser] = useState<Nullable<UserOption>>(null);

  const searchValue = sanitizeUserSearch(useDebounce(query, USER_SEARCH_DEBOUNCE));
  const typedValue = sanitizeUserSearch(query);
  const canSearch = searchValue.length >= USER_SEARCH_MIN_LENGTH;

  const { data: searchResult, isLoading: isSearching } = useSWR(
    canSearch ? ['searchUsers', searchValue] : null,
    userService.searchUsers,
    { onError: handleHttpError, shouldRetryOnError: false }
  );

  const isPicked = pickedUser?.id === userId;

  // The user came from the URL or from the table: only the id is known, the rest is loaded for display.
  // On 403/404 the raw id is shown, the filter itself keeps working.
  const { data: loadedUser } = useSWR(!isEmpty(userId) && !isPicked ? ['getUser', userId] : null, userService.getUser, {
    shouldRetryOnError: false,
  });

  const value = useMemo<Nullable<UserOption>>(() => {
    if (isEmpty(userId)) return null;
    if (isPicked) return pickedUser;

    return loadedUser ?? { id: userId, userNickname: userId };
  }, [userId, isPicked, pickedUser, loadedUser]);

  const options = useMemo(() => {
    const found = canSearch && typedValue === searchValue ? (searchResult?.result ?? []) : [];

    return value && !found.some((user) => user.id === value.id) ? [value, ...found] : found;
  }, [canSearch, typedValue, searchValue, searchResult, value]);

  const footer = canSearch ? getSearchFooter(searchResult) : null;

  const isLoading = typedValue.length >= USER_SEARCH_MIN_LENGTH && (typedValue !== searchValue || isSearching);

  const handleChange = (_: SyntheticEvent, user: Nullable<UserOption>) => {
    setPickedUser(user);
    onChange(user?.id ?? '');
  };

  const handleInputChange = (_: SyntheticEvent, inputValue: string, reason: AutocompleteInputChangeReason) => {
    // After a selection MUI puts the label into the input; that must not trigger a search.
    setQuery(reason === 'input' ? inputValue : '');
  };

  const details = value ? getUserDetails(value) : [];

  return (
    <PaperFooterContext.Provider value={footer}>
      <Autocomplete<UserOption>
        className={css.userPicker}
        size="small"
        value={value}
        options={options}
        loading={isLoading}
        filterOptions={(items) => items}
        getOptionLabel={(user) => user.userNickname}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={handleChange}
        onInputChange={handleInputChange}
        PaperComponent={PaperWithFooter}
        loadingText="Поиск…"
        noOptionsText={
          typedValue.length < USER_SEARCH_MIN_LENGTH
            ? `Введите минимум ${USER_SEARCH_MIN_LENGTH} символа`
            : 'Ничего не найдено'
        }
        renderOption={({ key, ...props }, user) => (
          <li key={key} {...props}>
            <div className={css.userOption}>
              <UserAvatar className={css.optionAvatar} imageUrl={user.imgUrl} />

              <div className={css.userOptionText}>
                <span className={css.nickname}>{user.userNickname}</span>

                {getUserDetails(user).map((detail) => (
                  <span key={detail} className={css.detail}>
                    {detail}
                  </span>
                ))}
              </div>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="User"
            placeholder="Nickname, email or telegram"
            InputProps={{
              ...params.InputProps,
              startAdornment: value && (
                <Tooltip title={details.join(' · ')} disableHoverListener={isEmpty(details)}>
                  <span className={css.inputAvatarWrapper}>
                    <UserAvatar className={css.inputAvatar} imageUrl={value.imgUrl} />
                  </span>
                </Tooltip>
              ),
            }}
          />
        )}
      />
    </PaperFooterContext.Provider>
  );
});

UserPicker.displayName = 'UserPicker';
